/**
 * Material Assignment Modal Component
 *
 * Allows admins to assign/remove materials to/from course groups.
 * Automatically links associated answers when assigning materials.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ModalOverlay, ModalContainer } from './BaseModal';
import {
  listMaterials,
  assignMaterial,
  unassignMaterial,
  listAvailableMaterials,
  calculateWeeks,
  updateMaterialSlot,
  updateMaterialAssignment,
  batchAssignMaterials,
  batchUnassignMaterials,
  termEndRemove,
  reAutoMatch
} from '../../api/courseGroupMaterialApi';
import { isSpecialTerm } from '../../api/courseGroupPortalApi';

// Helper function to get sorted slots with visual indices (for display)
// Sorts by date, then startTime, and assigns sequential visual indices starting from 1
const getSortedSlotsWithVisualIndex = (slots) => {
  if (!slots || slots.length === 0) return [];
  return [...slots]
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.startTime.localeCompare(b.startTime);
    })
    .map((slot, idx) => ({ ...slot, visualIndex: idx + 1 }));
};

// Helper function to get visual index for a specific slotIndex
const getVisualIndex = (slots, slotIndex) => {
  const sorted = getSortedSlotsWithVisualIndex(slots);
  const found = sorted.find(s => s.slotIndex === slotIndex);
  return found ? found.visualIndex : null;
};

// Styled components
const Modal = styled(ModalContainer)`
  max-width: 1200px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 12px 12px 0 0;

  h3 {
    margin: 0;
    font-size: 18px;
    color: white;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  line-height: 1;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;

  label {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
  }
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  background: white;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$color || '#10b981'};
  color: white;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MaterialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 12px;
`;

const MaterialCard = styled.div`
  background: ${props => props.$isAnswer ? '#fefce8' : '#f0fdf4'};
  border: 1px solid ${props => props.$isAnswer ? '#fef08a' : '#bbf7d0'};
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MaterialHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h5 {
    margin: 0;
    font-size: 14px;
    color: #1f2937;
    flex: 1;
    word-break: break-word;
  }
`;

const MaterialMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$bg || '#dcfce7'};
  color: ${props => props.$color || '#166534'};
  white-space: nowrap;
`;

const SmallButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.$borderColor || '#10b981'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  background: ${props => props.$bg || '#f0fdf4'};
  color: ${props => props.$color || '#059669'};
  align-self: flex-end;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled(SmallButton)`
  border-color: #ef4444;
  background: #fef2f2;
  color: #dc2626;
`;

const DuplicateButton = styled(SmallButton)`
  border-color: #3b82f6;
  background: #eff6ff;
  color: #2563eb;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  align-self: flex-end;
`;

const DuplicateSelector = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DuplicateSelectorTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #0369a1;
`;

const DuplicateSelectorActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 8px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  padding: 12px 20px;
  border: none;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#10b981' : '#6b7280'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? '#10b981' : 'transparent'};
  margin-bottom: -1px;

  &:hover {
    color: #10b981;
  }
`;

const WeekAssignmentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;

  label {
    font-size: 11px;
    color: #6b7280;
  }
`;

const WeekSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  background: white;
  flex: 1;
  max-width: 200px;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const TermInput = styled.input`
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  background: white;
  width: 56px;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const CarriedOverBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #1e40af;
  background: #dbeafe;
  padding: 3px 8px;
  border-radius: 4px;
`;

const CarriedOverNote = styled.span`
  font-size: 11px;
  color: #6b7280;
`;

const InvalidWeekWarning = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #dc2626;
  background: #fef2f2;
  padding: 2px 6px;
  border-radius: 4px;
`;

const SlotBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #8b5cf6;
  color: white;
  white-space: nowrap;
`;

const ScheduleSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  background: white;
  flex: 1;
  max-width: 220px;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.4;
`;

const MatchResultBanner = styled.div`
  padding: 12px 14px;
  background: ${props => props.$hasIssues ? '#fef3c7' : '#ecfdf5'};
  border: 1px solid ${props => props.$hasIssues ? '#f59e0b' : '#10b981'};
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: ${props => props.$hasIssues ? '#92400e' : '#065f46'};
  line-height: 1.5;
`;

const MatchResultDetails = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${props => props.$hasIssues ? '#fbbf24' : '#6ee7b7'};
  font-size: 12px;

  ul {
    margin: 4px 0 0 0;
    padding-left: 18px;
  }

  li {
    margin-bottom: 2px;
  }
`;

const ReAutoMatchButton = styled.button`
  padding: 6px 14px;
  border: 1px solid #3b82f6;
  border-radius: 6px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #dbeafe;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RemoveAllButton = styled.button`
  padding: 6px 14px;
  border: 1px solid #ef4444;
  border-radius: 6px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #fee2e2;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TermEndButton = styled.button`
  padding: 6px 14px;
  border: 1px solid #d97706;
  border-radius: 6px;
  background: #fffbeb;
  color: #b45309;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #fef3c7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100; /* 必须高于主 modal 的 zLayers.modalOverlay(2000)/modal(2001)，否则确认框被主弹窗盖住 */
`;

const ConfirmModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 460px;
  padding: 20px 22px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const ConfirmHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #b91c1c;
  }
`;

const ConfirmBody = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.55;

  strong {
    color: #111827;
  }

  p {
    margin: 0 0 10px 0;
  }

  ul {
    margin: 0 0 10px 0;
    padding-left: 20px;
    color: #6b7280;
    font-size: 13px;
  }
`;

const ConfirmFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #dc2626;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #b91c1c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BatchActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
`;

const SelectionCount = styled.span`
  font-size: 13px;
  color: #374151;
  font-weight: 500;
`;

const ScheduleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #dbeafe;
  color: #1e40af;
  white-space: nowrap;
`;

const DAY_LABELS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MaterialAssignmentModal = ({
  show,
  onClose,
  courseGroup,
  onUpdate
}) => {
  const [activeTab, setActiveTab] = useState('assigned');
  const [assignedMaterials, setAssignedMaterials] = useState([]);
  const [assignedAnswers, setAssignedAnswers] = useState([]);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Course weeks and schedules state
  const [courseWeeks, setCourseWeeks] = useState([]);
  const [totalWeeks, setTotalWeeks] = useState(0);
  const [courseSchedules, setCourseSchedules] = useState([]);

  // Batch selection state
  const [selectedMaterialIds, setSelectedMaterialIds] = useState(new Set());

  // Filter state for available materials
  const [filterSubjectId, setFilterSubjectId] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [filterWeek, setFilterWeek] = useState('');

  // Duplicate assignment state
  const [duplicatingMaterialId, setDuplicatingMaterialId] = useState(null);
  const [duplicateSlotIndex, setDuplicateSlotIndex] = useState('');
  const [duplicateWeek, setDuplicateWeek] = useState('');
  const [duplicateScheduleIndex, setDuplicateScheduleIndex] = useState('');

  // Auto-match result feedback
  const [matchResult, setMatchResult] = useState(null);

  // Remove-all confirmation modal
  const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);

  // Term-end-remove confirmation modal
  const [showTermEndConfirm, setShowTermEndConfirm] = useState(false);

  // Get course group subjects for filtering
  const courseGroupSubjects = React.useMemo(() => {
    const subjects = courseGroup?.subjects || [];
    const result = [];
    subjects.forEach(parent => {
      result.push({ id: parent.subjectId, name: parent.subjectName, isParent: true });
      (parent.children || []).forEach(child => {
        result.push({ id: child.subjectId, name: child.subjectName, isParent: false, parentName: parent.subjectName });
      });
    });
    return result;
  }, [courseGroup?.subjects]);

  // Load assigned materials and course weeks
  const loadAssigned = useCallback(async () => {
    if (!courseGroup?.id) return;

    try {
      // Load materials and weeks in parallel
      const [materialsData, weeksData] = await Promise.all([
        listMaterials(courseGroup.id),
        calculateWeeks(courseGroup.id)
      ]);

      const materials = materialsData.materials || [];
      setAssignedMaterials(materials);
      setCourseWeeks(weeksData.weeks || []);
      setTotalWeeks(weeksData.totalWeeks || 0);
      // Get schedules from the course group response
      setCourseSchedules(materialsData.courseGroup?.schedules || []);

      // Extract answers from linkedAnswers field of each material
      const allAnswers = [];
      const seenAnswerIds = new Set();
      materials.forEach(mat => {
        (mat.linkedAnswers || []).forEach(answer => {
          if (!seenAnswerIds.has(answer.id)) {
            seenAnswerIds.add(answer.id);
            allAnswers.push(answer);
          }
        });
      });
      setAssignedAnswers(allAnswers);
    } catch (err) {
      console.error('Error loading assigned materials:', err);
      setError(err.message);
    }
  }, [courseGroup?.id]);

  // Load available materials (filtered by course group subjects)
  const loadAvailable = useCallback(async () => {
    if (!courseGroup?.id) return;

    try {
      const filters = {
        courseGroupId: courseGroup.id,  // Filter to course group's subjects
        isAnswer: false  // Only show materials, not answers
      };
      if (filterSubjectId) filters.subjectId = filterSubjectId;
      if (filterYear) filters.year = parseInt(filterYear);
      if (filterTerm) filters.term = filterTerm === 'special' ? 'special' : parseInt(filterTerm);

      const data = await listAvailableMaterials(filters);

      // Filter out already assigned
      const assignedIds = new Set([
        ...assignedMaterials.map(m => m.id),
        ...assignedAnswers.map(a => a.id)
      ]);
      const available = data.filter(m => !assignedIds.has(m.id));

      // Apply week filter only for non-special terms
      if (filterWeek && filterTerm !== 'special') {
        const weekNum = parseInt(filterWeek);
        setAvailableMaterials(available.filter(m => m.week === weekNum));
      } else {
        setAvailableMaterials(available);
      }
    } catch (err) {
      console.error('Error loading available materials:', err);
    }
  }, [courseGroup?.id, filterSubjectId, filterYear, filterTerm, filterWeek, assignedMaterials, assignedAnswers]);

  // Initial load
  useEffect(() => {
    if (show) {
      setLoading(true);
      loadAssigned().finally(() => setLoading(false));
    }
  }, [show, loadAssigned]);

  // Load available when filters change or tab switches
  useEffect(() => {
    if (show && activeTab === 'available') {
      loadAvailable();
    }
  }, [show, activeTab, loadAvailable]);

  // Set default filters based on course group
  useEffect(() => {
    if (courseGroup) {
      setFilterSubjectId('');  // Show all course group subjects by default
      setFilterYear(courseGroup.year?.toString() || '');
      // Handle special term properly
      setFilterTerm(isSpecialTerm(courseGroup.term) ? 'special' : courseGroup.term?.toString() || '');
    }
  }, [courseGroup]);

  // Check if a week number is valid (exists in course weeks and not a holiday)
  const isWeekValid = useCallback((weekNum) => {
    if (!weekNum) return true;  // Unassigned is always valid
    const week = courseWeeks.find(w => w.week === weekNum);
    return week && !week.isHoliday;
  }, [courseWeeks]);

  // Handle week assignment change
  const handleWeekChange = async (assignmentId, weekValue, currentScheduleIndex) => {
    const newWeek = weekValue === '' ? null : parseInt(weekValue);

    // Preserve the existing schedule selection instead of clearing it.
    // If none was set and there's exactly one schedule option, default to it.
    let scheduleIndexToKeep = null;
    if (newWeek !== null) {
      scheduleIndexToKeep = currentScheduleIndex ?? null;
      if (scheduleIndexToKeep === null && courseSchedules.length === 1) {
        scheduleIndexToKeep = 0;
      }
    }

    setSaving(true);
    try {
      await updateMaterialAssignment(assignmentId, newWeek, scheduleIndexToKeep);
      await loadAssigned();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating material week:', err);
      alert(`Failed to update week: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle slot assignment change (for special term)
  const handleSlotChange = async (assignmentId, slotValue) => {
    const newSlotIndex = slotValue === '' ? null : parseInt(slotValue);

    setSaving(true);
    try {
      await updateMaterialSlot(assignmentId, newSlotIndex);
      await loadAssigned();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating material slot:', err);
      alert(`Failed to update slot: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle schedule assignment change (for regular term)
  const handleScheduleChange = async (assignmentId, scheduleValue, currentWeek) => {
    const newScheduleIndex = scheduleValue === '' ? null : parseInt(scheduleValue);

    setSaving(true);
    try {
      await updateMaterialAssignment(assignmentId, currentWeek, newScheduleIndex);
      await loadAssigned();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating material schedule:', err);
      alert(`Failed to update schedule: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Manual override of which term-cycle this assignment belongs to (defaults
  // to the course group's current term at assign time — see assignMaterial /
  // batchAssignMaterials). Mainly for fixing up a "Term End Remove" result by
  // hand; most assignments never need this touched.
  const handleTermChange = async (assignmentId, termValue, currentWeek, currentScheduleIndex) => {
    const newTerm = termValue === '' ? null : parseInt(termValue);
    setSaving(true);
    try {
      await updateMaterialAssignment(assignmentId, currentWeek, currentScheduleIndex, newTerm);
      await loadAssigned();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating material term:', err);
      alert(`Failed to update term: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Get subject name from course group subjects
  const getSubjectNameById = useCallback((subjectId) => {
    if (!subjectId) return '';
    for (const subject of courseGroupSubjects) {
      if (subject.id === subjectId) {
        return subject.name;
      }
    }
    return '';
  }, [courseGroupSubjects]);

  // Batch selection handlers
  const toggleMaterialSelection = (materialId) => {
    setSelectedMaterialIds(prev => {
      const next = new Set(prev);
      if (next.has(materialId)) {
        next.delete(materialId);
      } else {
        next.add(materialId);
      }
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedMaterialIds(new Set(availableMaterials.map(m => m.id)));
  };

  const clearSelection = () => {
    setSelectedMaterialIds(new Set());
  };

  // Handle batch assign (always auto-match)
  const handleBatchAssign = async () => {
    if (selectedMaterialIds.size === 0) return;

    setSaving(true);
    setMatchResult(null);
    try {
      const result = await batchAssignMaterials(
        courseGroup.id,
        Array.from(selectedMaterialIds),
        true  // always auto-match
      );

      clearSelection();
      await loadAssigned();
      await loadAvailable();
      if (onUpdate) onUpdate();

      // Show detailed match result as banner instead of alert
      setMatchResult({
        type: 'assign',
        assignedCount: result.assignedCount,
        skippedCount: result.skippedCount,
        autoMatchedCount: result.autoMatchedCount,
        unmatchedDetails: result.unmatchedDetails,
        unmatchedReasonCounts: result.unmatchedReasonCounts,
        hasSchedulesWithoutSubject: result.hasSchedulesWithoutSubject
      });
      setActiveTab('assigned');
    } catch (err) {
      console.error('Error batch assigning materials:', err);
      alert(`Failed to assign materials: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle assign material (with auto-match to week/schedule)
  const handleAssign = async (materialId) => {
    setSaving(true);
    setMatchResult(null);
    try {
      const result = await batchAssignMaterials(courseGroup.id, [materialId], true);
      await loadAssigned();
      await loadAvailable();
      if (onUpdate) onUpdate();

      // Show match result feedback
      setMatchResult({
        type: 'assign',
        assignedCount: result.assignedCount,
        skippedCount: result.skippedCount,
        autoMatchedCount: result.autoMatchedCount,
        unmatchedDetails: result.unmatchedDetails,
        unmatchedReasonCounts: result.unmatchedReasonCounts,
        hasSchedulesWithoutSubject: result.hasSchedulesWithoutSubject
      });
      setActiveTab('assigned');
    } catch (err) {
      console.error('Error assigning material:', err);
      alert(`Failed to assign material: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle re-auto-match for unmatched assigned materials
  const handleReAutoMatch = async () => {
    setSaving(true);
    setMatchResult(null);
    try {
      const result = await reAutoMatch(courseGroup.id);
      await loadAssigned();
      if (onUpdate) onUpdate();

      setMatchResult({
        type: 'reAutoMatch',
        matchedCount: result.matchedCount,
        skippedCount: result.skippedCount,
        unmatchedDetails: result.unmatchedDetails,
        unmatchedReasonCounts: result.unmatchedReasonCounts
      });
    } catch (err) {
      console.error('Error re-auto-matching:', err);
      alert(`Failed to re-auto-match: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Helper: format unmatched reason for display
  const formatUnmatchedReason = (reason) => {
    const isSpecial = isSpecialTerm(courseGroup?.term);
    const reasonMap = {
      'no_subject': 'Material has no subject assigned',
      'subject_mismatch': `Material subject doesn't match any ${isSpecial ? 'slot' : 'schedule'} subject`,
      'schedule_no_subject': `Some schedules have no subject set - please edit the course group to assign subjects to schedules`,
      'slot_no_subject': `Some slots have no subject set - please edit the course group to assign subjects to slots`,
      'no_week': 'Material has no week number',
      'week_overflow': 'Material week is past the last teaching week (extend the course finish date, or assign it manually)'
    };
    return reasonMap[reason] || reason;
  };

  // Check if schedules/slots are missing subjects
  const hasMissingSubjects = React.useMemo(() => {
    if (!courseGroup) return false;
    const isSpecial = isSpecialTerm(courseGroup.term);
    if (isSpecial) {
      return (courseGroup.slots || []).some(s => !s.subjectId);
    } else {
      return (courseGroup.schedules || []).some(s => !s.subjectId);
    }
  }, [courseGroup]);

  // Carried over from the previous term by Term End Remove / Term End Adjust:
  // its assignedWeek (9/10) is the OLD term's week, so the new term's week
  // dropdown would misread it — show it read-only instead.
  const isCarriedOver = useCallback((m) => (
    !isSpecialTerm(courseGroup?.term) &&
    m.assignedTerm !== null && m.assignedTerm !== undefined &&
    courseGroup?.term !== null && courseGroup?.term !== undefined &&
    Number(m.assignedTerm) !== Number(courseGroup.term)
  ), [courseGroup?.term]);

  // Count unmatched assigned materials
  const unmatchedCount = React.useMemo(() => {
    const isSpecial = isSpecialTerm(courseGroup?.term);
    return assignedMaterials.filter(m => {
      if (isCarriedOver(m)) return false;
      if (isSpecial) {
        return m.assignedSlotIndex === null || m.assignedSlotIndex === undefined;
      } else {
        return (m.assignedWeek === null || m.assignedWeek === undefined) ||
               (m.assignedScheduleIndex === null || m.assignedScheduleIndex === undefined);
      }
    }).length;
  }, [assignedMaterials, courseGroup?.term, isCarriedOver]);

  // Handle duplicate assignment - assign same material to another slot/week
  const handleDuplicateAssign = async (materialId) => {
    const isSpecial = isSpecialTerm(courseGroup?.term);

    // Validate selection
    if (isSpecial) {
      if (!duplicateSlotIndex) {
        alert('Please select a slot');
        return;
      }
    } else {
      if (!duplicateWeek) {
        alert('Please select a week');
        return;
      }
    }

    setSaving(true);
    try {
      if (isSpecial) {
        await assignMaterial(courseGroup.id, materialId, null, null, parseInt(duplicateSlotIndex));
      } else {
        const week = parseInt(duplicateWeek);
        const scheduleIndex = duplicateScheduleIndex ? parseInt(duplicateScheduleIndex) : null;
        await assignMaterial(courseGroup.id, materialId, week, scheduleIndex, null);
      }

      // Reset duplicate state
      setDuplicatingMaterialId(null);
      setDuplicateSlotIndex('');
      setDuplicateWeek('');
      setDuplicateScheduleIndex('');

      await loadAssigned();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error duplicating material assignment:', err);
      alert(`Failed to duplicate assignment: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Cancel duplicate operation
  const cancelDuplicate = () => {
    setDuplicatingMaterialId(null);
    setDuplicateSlotIndex('');
    setDuplicateWeek('');
    setDuplicateScheduleIndex('');
  };

  // Handle remove-all (opens confirmation modal)
  const handleRemoveAllClick = () => {
    if (assignedMaterials.length === 0) return;
    setShowRemoveAllConfirm(true);
  };

  const confirmRemoveAll = async () => {
    if (!courseGroup?.id) return;
    setSaving(true);
    try {
      const result = await batchUnassignMaterials(courseGroup.id);
      setShowRemoveAllConfirm(false);
      setMatchResult(null);
      await loadAssigned();
      await loadAvailable();
      if (onUpdate) onUpdate();
      if (result?.deletedCount > 0) {
        alert(`Removed ${result.deletedCount} material assignment(s).`);
      }
    } catch (err) {
      console.error('Error removing all materials:', err);
      alert(`Failed to remove all materials: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle term-end-remove (opens confirmation modal)
  const handleTermEndClick = () => {
    if (assignedMaterials.length === 0) return;
    setShowTermEndConfirm(true);
  };

  const confirmTermEnd = async () => {
    if (!courseGroup?.id) return;
    setSaving(true);
    try {
      const result = await termEndRemove(courseGroup.id);
      setShowTermEndConfirm(false);
      setMatchResult(null);
      await loadAssigned();
      await loadAvailable();
      if (onUpdate) onUpdate();
      alert(result.message || `Kept Week 9 & 10, removed ${result.removedCount} other assignment(s).`);
    } catch (err) {
      console.error('Error running term end remove:', err);
      alert(`Failed to run term end remove: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle unassign material
  const handleUnassign = async (assignmentId, materialName) => {
    if (!window.confirm(`Are you sure you want to remove "${materialName}" from this course group?`)) {
      return;
    }

    setSaving(true);
    try {
      await unassignMaterial(assignmentId);
      await loadAssigned();
      await loadAvailable();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error unassigning material:', err);
      alert(`Failed to unassign material: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const courseGroupName = courseGroup
    ? `Y${courseGroup.year}${isSpecialTerm(courseGroup.term) ? ' Special' : `T${courseGroup.term}`}`
    : '';

  return (
    <>
    <ModalOverlay>
      <Modal>
        <ModalHeader>
          <h3>Manage Materials - {courseGroupName}</h3>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && <ErrorState>{error}</ErrorState>}

          {loading ? (
            <LoadingState>Loading...</LoadingState>
          ) : (
            <>
              <TabContainer>
                <Tab
                  $active={activeTab === 'assigned'}
                  onClick={() => setActiveTab('assigned')}
                >
                  Assigned ({assignedMaterials.length + assignedAnswers.length})
                </Tab>
                <Tab
                  $active={activeTab === 'available'}
                  onClick={() => setActiveTab('available')}
                >
                  Add Materials
                </Tab>
              </TabContainer>

              {activeTab === 'assigned' ? (
                <>
                  {/* Warning: schedules/slots missing subjects */}
                  {hasMissingSubjects && (
                    <WarningBanner>
                      <span style={{ fontSize: '16px' }}>!</span>
                      <div>
                        <strong>Some {isSpecialTerm(courseGroup?.term) ? 'slots' : 'schedules'} have no subject assigned.</strong>
                        {' '}Auto-matching may be incomplete. Please edit the course group to assign subjects to all {isSpecialTerm(courseGroup?.term) ? 'slots' : 'schedules'}.
                      </div>
                    </WarningBanner>
                  )}

                  {/* Auto-match result feedback */}
                  {matchResult && (
                    <MatchResultBanner $hasIssues={matchResult.unmatchedDetails?.length > 0}>
                      {matchResult.type === 'assign' ? (
                        <div>
                          <strong>Added {matchResult.assignedCount} material(s)</strong>
                          {matchResult.skippedCount > 0 && `, ${matchResult.skippedCount} skipped (already assigned)`}
                          {matchResult.autoMatchedCount > 0 && `, ${matchResult.autoMatchedCount} auto-matched`}
                          {matchResult.assignedCount > 0 && matchResult.autoMatchedCount === 0 && matchResult.assignedCount !== matchResult.skippedCount &&
                            ' (none could be auto-matched)'}
                        </div>
                      ) : (
                        <div>
                          <strong>Re-auto-match complete:</strong> {matchResult.matchedCount} material(s) matched
                          {matchResult.skippedCount > 0 && `, ${matchResult.skippedCount} already assigned`}
                        </div>
                      )}
                      {matchResult.unmatchedDetails?.length > 0 && (
                        <MatchResultDetails $hasIssues>
                          <strong>Could not match {matchResult.unmatchedDetails.length} material(s):</strong>
                          <ul>
                            {Object.entries(matchResult.unmatchedReasonCounts || {}).map(([reason, count]) => (
                              <li key={reason}>{count}x {formatUnmatchedReason(reason)}</li>
                            ))}
                          </ul>
                        </MatchResultDetails>
                      )}
                      <div style={{ marginTop: '6px' }}>
                        <button
                          onClick={() => setMatchResult(null)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'inherit', textDecoration: 'underline', padding: 0 }}
                        >
                          Dismiss
                        </button>
                      </div>
                    </MatchResultBanner>
                  )}

                  {/* Assigned Materials */}
                  <Section>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <SectionTitle style={{ marginBottom: 0 }}>Materials ({assignedMaterials.length})</SectionTitle>
                      <HeaderActions>
                        {unmatchedCount > 0 && (
                          <ReAutoMatchButton onClick={handleReAutoMatch} disabled={saving}>
                            Re-auto-match ({unmatchedCount} unmatched)
                          </ReAutoMatchButton>
                        )}
                        {assignedMaterials.length > 0 && !isSpecialTerm(courseGroup?.term) && (
                          <TermEndButton onClick={handleTermEndClick} disabled={saving}>
                            Term End Remove
                          </TermEndButton>
                        )}
                        {assignedMaterials.length > 0 && (
                          <RemoveAllButton onClick={handleRemoveAllClick} disabled={saving}>
                            Remove All
                          </RemoveAllButton>
                        )}
                      </HeaderActions>
                    </div>
                    {isSpecialTerm(courseGroup?.term) ? (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                        Special term course with {courseGroup?.slots?.length || 0} class slots. Assign materials to specific slots below.
                      </div>
                    ) : totalWeeks > 0 && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                        Course has {totalWeeks} weeks. Assign materials to specific weeks below.
                      </div>
                    )}
                    {assignedMaterials.length === 0 ? (
                      <EmptyState>No materials assigned yet.</EmptyState>
                    ) : (
                      <MaterialGrid>
                        {assignedMaterials.map(material => {
                          const carriedOver = isCarriedOver(material);
                          const weekInvalid = !carriedOver && !isSpecialTerm(courseGroup?.term) && material.assignedWeek && !isWeekValid(material.assignedWeek);
                          return (
                            <MaterialCard key={material.assignmentId}>
                              <MaterialHeader>
                                <h5>{material.name}</h5>
                                <Badge>Original W{material.week}</Badge>
                              </MaterialHeader>
                              <MaterialMeta>
                                {material.subjectName} | Y{material.year}T{material.term}
                              </MaterialMeta>
                              <MaterialMeta>
                                Uploaded by {material.uploaderName || 'Unknown'}
                              </MaterialMeta>

                              {/* Week/Slot Assignment */}
                              <WeekAssignmentRow>
                                <label>Assigned to:</label>
                                {isSpecialTerm(courseGroup?.term) ? (
                                  // Slot assignment for special term
                                  <WeekSelect
                                    value={material.assignedSlotIndex || ''}
                                    onChange={(e) => handleSlotChange(material.assignmentId, e.target.value)}
                                    disabled={saving}
                                  >
                                    <option value="">Not assigned to slot</option>
                                    {getSortedSlotsWithVisualIndex(courseGroup?.slots || []).map(slot => (
                                      <option key={slot.slotIndex} value={slot.slotIndex}>
                                        Slot #{slot.visualIndex} ({slot.date} {slot.startTime}-{slot.endTime})
                                      </option>
                                    ))}
                                  </WeekSelect>
                                ) : carriedOver ? (
                                  // Retained from the previous term — read-only
                                  <>
                                    <CarriedOverBadge>
                                      T{material.assignedTerm} · W{material.assignedWeek ?? material.week} (previous term)
                                    </CarriedOverBadge>
                                    <CarriedOverNote>
                                      Kept from last term. Parents can open its answers; teachers can open it until the end of Week 1.
                                    </CarriedOverNote>
                                  </>
                                ) : (
                                  // Week and schedule assignment for regular term
                                  <>
                                    <WeekSelect
                                      value={material.assignedWeek || ''}
                                      onChange={(e) => handleWeekChange(material.assignmentId, e.target.value, material.assignedScheduleIndex)}
                                      disabled={saving}
                                    >
                                      <option value="">Not assigned to week</option>
                                      {courseWeeks.filter(w => !w.isHoliday).map(week => (
                                        <option key={week.week} value={week.week}>
                                          Week {week.week} ({week.startDate} - {week.endDate})
                                        </option>
                                      ))}
                                    </WeekSelect>
                                    {/* Schedule selector - only show if week is assigned and schedules exist */}
                                    {material.assignedWeek && courseSchedules.length > 0 && (
                                      <ScheduleSelect
                                        value={material.assignedScheduleIndex ?? ''}
                                        onChange={(e) => handleScheduleChange(material.assignmentId, e.target.value, material.assignedWeek)}
                                        disabled={saving}
                                        style={material.assignedScheduleIndex === null || material.assignedScheduleIndex === undefined ? { borderColor: '#f59e0b' } : {}}
                                      >
                                        <option value="">Not assigned</option>
                                        {courseSchedules.map((sched, idx) => (
                                          <option key={idx} value={idx}>
                                            {DAY_LABELS[sched.dayOfWeek] || `Day ${sched.dayOfWeek}`} {sched.startTime}-{sched.endTime}
                                            {sched.subjectId && ` (${getSubjectNameById(sched.subjectId)})`}
                                          </option>
                                        ))}
                                      </ScheduleSelect>
                                    )}
                                    {/* Which term-cycle this assignment belongs to. Defaults to
                                        the course group's current term when assigned; editable
                                        here for the rare manual fix-up (e.g. after Term End
                                        Remove, or a material deliberately carried over early). */}
                                    {material.assignedWeek && (
                                      <TermInput
                                        type="number"
                                        title="Term this assignment belongs to"
                                        placeholder="Term"
                                        value={material.assignedTerm ?? ''}
                                        onChange={(e) => handleTermChange(
                                          material.assignmentId, e.target.value,
                                          material.assignedWeek, material.assignedScheduleIndex
                                        )}
                                        disabled={saving}
                                      />
                                    )}
                                  </>
                                )}
                                {material.assignedSlotIndex && isSpecialTerm(courseGroup?.term) && (
                                  <SlotBadge>Slot #{getVisualIndex(courseGroup?.slots, material.assignedSlotIndex) || material.assignedSlotIndex}</SlotBadge>
                                )}
                                {!carriedOver && material.assignedScheduleIndex !== undefined && material.assignedScheduleIndex !== null && !isSpecialTerm(courseGroup?.term) && (
                                  <ScheduleBadge>
                                    {courseSchedules[material.assignedScheduleIndex] && (
                                      <>
                                        {DAY_LABELS[courseSchedules[material.assignedScheduleIndex].dayOfWeek]}
                                        {' '}
                                        {courseSchedules[material.assignedScheduleIndex].startTime}
                                      </>
                                    )}
                                  </ScheduleBadge>
                                )}
                                {weekInvalid && (
                                  <InvalidWeekWarning>
                                    Week {material.assignedWeek} invalid
                                  </InvalidWeekWarning>
                                )}
                                {/* Warning when schedule not assigned for regular term */}
                                {!carriedOver && !isSpecialTerm(courseGroup?.term) && material.assignedWeek && courseSchedules.length > 0 &&
                                  (material.assignedScheduleIndex === null || material.assignedScheduleIndex === undefined) && (
                                  <InvalidWeekWarning style={{ background: '#fef3c7', color: '#92400e' }}>
                                    Schedule not assigned
                                  </InvalidWeekWarning>
                                )}
                              </WeekAssignmentRow>

                              {/* Duplicate selector - show when duplicating this assignment */}
                              {duplicatingMaterialId === material.assignmentId && (
                                <DuplicateSelector>
                                  <DuplicateSelectorTitle>Add to another {isSpecialTerm(courseGroup?.term) ? 'slot' : 'week'}:</DuplicateSelectorTitle>
                                  <DuplicateSelectorActions>
                                    {isSpecialTerm(courseGroup?.term) ? (
                                      <WeekSelect
                                        value={duplicateSlotIndex}
                                        onChange={(e) => setDuplicateSlotIndex(e.target.value)}
                                        disabled={saving}
                                        style={{ flex: 1 }}
                                      >
                                        <option value="">Select slot...</option>
                                        {getSortedSlotsWithVisualIndex(courseGroup?.slots || []).map(slot => (
                                          <option key={slot.slotIndex} value={slot.slotIndex}>
                                            Slot #{slot.visualIndex} ({slot.date} {slot.startTime}-{slot.endTime})
                                          </option>
                                        ))}
                                      </WeekSelect>
                                    ) : (
                                      <>
                                        <WeekSelect
                                          value={duplicateWeek}
                                          onChange={(e) => setDuplicateWeek(e.target.value)}
                                          disabled={saving}
                                          style={{ flex: 1 }}
                                        >
                                          <option value="">Select week...</option>
                                          {courseWeeks.filter(w => !w.isHoliday).map(week => (
                                            <option key={week.week} value={week.week}>
                                              Week {week.week}
                                            </option>
                                          ))}
                                        </WeekSelect>
                                        {duplicateWeek && courseSchedules.length > 0 && (
                                          <ScheduleSelect
                                            value={duplicateScheduleIndex}
                                            onChange={(e) => setDuplicateScheduleIndex(e.target.value)}
                                            disabled={saving}
                                            style={{ flex: 1 }}
                                          >
                                            <option value="">Any schedule</option>
                                            {courseSchedules.map((sched, idx) => (
                                              <option key={idx} value={idx}>
                                                {DAY_LABELS[sched.dayOfWeek]} {sched.startTime}
                                              </option>
                                            ))}
                                          </ScheduleSelect>
                                        )}
                                      </>
                                    )}
                                    <SmallButton
                                      onClick={() => handleDuplicateAssign(material.id)}
                                      disabled={saving}
                                    >
                                      Add
                                    </SmallButton>
                                    <SmallButton
                                      onClick={cancelDuplicate}
                                      disabled={saving}
                                      $borderColor="#6b7280"
                                      $bg="#f9fafb"
                                      $color="#374151"
                                    >
                                      Cancel
                                    </SmallButton>
                                  </DuplicateSelectorActions>
                                </DuplicateSelector>
                              )}

                              <ButtonRow>
                                <DuplicateButton
                                  onClick={() => setDuplicatingMaterialId(duplicatingMaterialId === material.assignmentId ? null : material.assignmentId)}
                                  disabled={saving}
                                >
                                  {duplicatingMaterialId === material.assignmentId ? 'Cancel' : 'Duplicate'}
                                </DuplicateButton>
                                <RemoveButton
                                  onClick={() => handleUnassign(material.assignmentId, material.name)}
                                  disabled={saving}
                                >
                                  Remove
                                </RemoveButton>
                              </ButtonRow>
                            </MaterialCard>
                          );
                        })}
                      </MaterialGrid>
                    )}
                  </Section>

                  {/* Linked Answers (read-only, fetched from MaterialAnswerPortal) */}
                  <Section>
                    <SectionTitle>Linked Answers ({assignedAnswers.length})</SectionTitle>
                    {assignedAnswers.length === 0 ? (
                      <EmptyState>No answers linked to assigned materials. Link answers in Materials Management.</EmptyState>
                    ) : (
                      <MaterialGrid>
                        {assignedAnswers.map(answer => (
                          <MaterialCard key={answer.id} $isAnswer>
                            <MaterialHeader>
                              <h5>{answer.name}</h5>
                              <Badge $bg="#fef9c3" $color="#854d0e">W{answer.assignedWeek ?? answer.week} Answer</Badge>
                            </MaterialHeader>
                            <MaterialMeta>
                              {answer.subject} | Y{answer.year}T{answer.term}
                            </MaterialMeta>
                          </MaterialCard>
                        ))}
                      </MaterialGrid>
                    )}
                  </Section>
                </>
              ) : (
                <>
                  {/* Filters */}
                  <FilterRow>
                    <FilterGroup>
                      <label>Subject</label>
                      <Select
                        value={filterSubjectId}
                        onChange={(e) => setFilterSubjectId(e.target.value)}
                      >
                        <option value="">All Course Group Subjects</option>
                        {courseGroupSubjects.map(subject => (
                          <option key={subject.id} value={subject.id}>
                            {subject.isParent ? subject.name : `${subject.parentName} - ${subject.name}`}
                          </option>
                        ))}
                      </Select>
                    </FilterGroup>
                    <FilterGroup>
                      <label>Year</label>
                      <Select
                        value={filterYear}
                        onChange={(e) => setFilterYear(e.target.value)}
                      >
                        <option value="">All Years</option>
                        {Array.from({ length: 16 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>Year {i + 1}</option>
                        ))}
                      </Select>
                    </FilterGroup>
                    <FilterGroup>
                      <label>Term</label>
                      <Select
                        value={filterTerm}
                        onChange={(e) => setFilterTerm(e.target.value)}
                      >
                        <option value="">All Terms</option>
                        <option value="1">Term 1</option>
                        <option value="2">Term 2</option>
                        <option value="3">Term 3</option>
                        <option value="4">Term 4</option>
                        <option value="special">Special Term</option>
                      </Select>
                    </FilterGroup>
                    {filterTerm !== 'special' && (
                      <FilterGroup>
                        <label>Week</label>
                        <Select
                          value={filterWeek}
                          onChange={(e) => setFilterWeek(e.target.value)}
                        >
                          <option value="">All Weeks</option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>Week {i + 1}</option>
                          ))}
                        </Select>
                      </FilterGroup>
                    )}
                  </FilterRow>

                  {/* Available Materials */}
                  <Section>
                    <SectionTitle>Available Materials ({availableMaterials.length})</SectionTitle>
                    {availableMaterials.length === 0 ? (
                      <EmptyState>No materials match your filters or all are already assigned.</EmptyState>
                    ) : (
                      <>
                        {/* Batch Action Bar */}
                        <BatchActionBar>
                          <Checkbox
                            checked={selectedMaterialIds.size === availableMaterials.length && availableMaterials.length > 0}
                            onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                            disabled={saving}
                          />
                          <SelectionCount>
                            {selectedMaterialIds.size > 0
                              ? `${selectedMaterialIds.size} selected`
                              : 'Select all'}
                          </SelectionCount>
                          {selectedMaterialIds.size > 0 && (
                            <>
                              <Button
                                onClick={() => handleBatchAssign()}
                                disabled={saving}
                                style={{ padding: '6px 12px', fontSize: '12px' }}
                              >
                                Add Selected
                              </Button>
                              <SmallButton
                                onClick={clearSelection}
                                disabled={saving}
                                $borderColor="#6b7280"
                                $bg="#f9fafb"
                                $color="#374151"
                              >
                                Clear
                              </SmallButton>
                            </>
                          )}
                        </BatchActionBar>

                        <MaterialGrid>
                          {availableMaterials.map(material => (
                            <MaterialCard key={material.id}>
                              <MaterialHeader>
                                <Checkbox
                                  checked={selectedMaterialIds.has(material.id)}
                                  onChange={() => toggleMaterialSelection(material.id)}
                                  disabled={saving}
                                  style={{ marginRight: '8px' }}
                                />
                                <h5>{material.name}</h5>
                                <Badge>W{material.week}</Badge>
                              </MaterialHeader>
                              <MaterialMeta>
                                {material.subject} | Y{material.year}T{material.term}
                              </MaterialMeta>
                              <MaterialMeta>
                                Uploaded by {material.uploaderName || 'Unknown'}
                              </MaterialMeta>
                              <SmallButton
                                onClick={() => handleAssign(material.id)}
                                disabled={saving}
                              >
                                Add
                              </SmallButton>
                            </MaterialCard>
                          ))}
                        </MaterialGrid>
                      </>
                    )}
                  </Section>
                </>
              )}
            </>
          )}
        </ModalContent>

        <ModalFooter>
          <Button $color="#6b7280" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </ModalOverlay>

    {showRemoveAllConfirm && (
      <ConfirmOverlay>
        <ConfirmModal>
          <ConfirmHeader>
            <h3>Remove All Assigned Materials?</h3>
            <CloseButton
              style={{ color: '#6b7280', fontSize: '24px' }}
              onClick={() => !saving && setShowRemoveAllConfirm(false)}
              disabled={saving}
            >
              &times;
            </CloseButton>
          </ConfirmHeader>
          <ConfirmBody>
            <p>
              This will remove <strong>all {assignedMaterials.length} assigned material(s)</strong>
              {' '}from <strong>{courseGroupName}</strong>.
            </p>
            <ul>
              <li>Linked answers shown under this course group will also disappear (they are derived from assigned materials).</li>
              <li>Existing student access records are not deleted.</li>
              <li>This action cannot be undone.</li>
            </ul>
            <p>Are you sure you want to continue?</p>
          </ConfirmBody>
          <ConfirmFooter>
            <CancelButton
              onClick={() => setShowRemoveAllConfirm(false)}
              disabled={saving}
            >
              Cancel
            </CancelButton>
            <DangerButton onClick={confirmRemoveAll} disabled={saving}>
              {saving ? 'Removing...' : 'Remove All'}
            </DangerButton>
          </ConfirmFooter>
        </ConfirmModal>
      </ConfirmOverlay>
    )}

    {showTermEndConfirm && (
      <ConfirmOverlay>
        <ConfirmModal>
          <ConfirmHeader>
            <h3>End Term {courseGroup?.term} and Prep for Next Term?</h3>
            <CloseButton
              style={{ color: '#6b7280', fontSize: '24px' }}
              onClick={() => !saving && setShowTermEndConfirm(false)}
              disabled={saving}
            >
              &times;
            </CloseButton>
          </ConfirmHeader>
          <ConfirmBody>
            <p>
              This prepares <strong>{courseGroupName}</strong> for its next term by:
            </p>
            <ul>
              <li>Keeping whatever is currently assigned to <strong>Week 9</strong> and <strong>Week 10</strong> — tagged as Term {courseGroup?.term} so continuing students' parents can still open those answers after the term rolls over, and visible to teachers only through the new term's first week.</li>
              <li>Removing every other material assignment (all other weeks) from this course group.</li>
              <li>If this has been run before, whatever it kept last time is removed too — only the most recent term's Week 9/10 stay retained.</li>
              <li>This action cannot be undone.</li>
            </ul>
            <p>Are you sure you want to continue?</p>
          </ConfirmBody>
          <ConfirmFooter>
            <CancelButton
              onClick={() => setShowTermEndConfirm(false)}
              disabled={saving}
            >
              Cancel
            </CancelButton>
            <DangerButton onClick={confirmTermEnd} disabled={saving}>
              {saving ? 'Processing...' : 'Term End Remove'}
            </DangerButton>
          </ConfirmFooter>
        </ConfirmModal>
      </ConfirmOverlay>
    )}
    </>
  );
};

export default MaterialAssignmentModal;
